import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogIn, UserPlus, Mail, Lock, User as UserIcon } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { useCustomerAuth, formatApiError } from "@/context/CustomerAuthContext";

export const AuthPage = ({ mode: initialMode = "login" }) => {
  const [mode, setMode] = useState(initialMode); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/account";
  const { me, login, register, googleStart } = useCustomerAuth();

  useEffect(() => {
    if (me) nav(nextPath, { replace: true });
  }, [me, nextPath, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        await register({ email, password, name });
        toast.success("Welcome to Anabiya Decor");
      } else {
        await login({ email, password });
        toast.success("Welcome back");
      }
      nav(nextPath, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Sign in failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="auth-page" className="relative min-h-screen bg-white">
      <Seo path={mode === "signup" ? "/account/signup" : "/account/login"}
           title={mode === "signup" ? "Create your Anabiya account" : "Sign in to your Anabiya account"}
           noindex />
      <AnnouncementBar />
      <Navbar />

      <section className="relative min-h-[calc(100svh-6rem)] grid grid-cols-12 gap-0 bg-bone">
        {/* Editorial column */}
        <aside className="hidden lg:flex col-span-5 xl:col-span-6 bg-forest text-champagne relative overflow-hidden p-16 flex-col justify-between">
          <div className="overline text-gold">§ Anabiya Trade Portal</div>
          <div>
            <div className="font-editorial italic text-gold text-2xl mb-4">Trusted by hotels, resorts &amp; yachts.</div>
            <h1 className="font-display text-champagne text-5xl xl:text-6xl leading-[1.02] tracking-[-0.02em]">
              Faster reorders.<br />
              <span className="italic font-editorial text-gold">Kept close.</span>
            </h1>
            <p className="mt-6 text-champagne/75 text-base max-w-md leading-relaxed">
              Sign in to see wholesale price sheets, reorder past shipments in one tap and pick up your last chat thread with our atelier.
            </p>
          </div>
          <div className="overline text-champagne/50">Est · Mumbai · 2016</div>
        </aside>

        {/* Form column */}
        <div className="col-span-12 lg:col-span-7 xl:col-span-6 flex items-center justify-center p-6 md:p-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="w-full max-w-md"
          >
            <div className="overline text-gold mb-2">
              {mode === "signup" ? "§ Create account" : "§ Sign in"}
            </div>
            <h2 className="font-display text-forest text-4xl md:text-5xl leading-none tracking-tight">
              {mode === "signup" ? "Welcome to the House." : "Welcome back."}
            </h2>
            <p className="mt-4 text-forest/70 text-sm max-w-sm">
              {mode === "signup"
                ? "One account for your quotes, samples, orders and chat with our atelier."
                : "Sign in to see your quotes, orders and pick up your chat thread."}
            </p>

            {/* Google button */}
            <button
              type="button"
              onClick={googleStart}
              data-testid="auth-google"
              className="mt-8 w-full inline-flex items-center justify-center gap-3 border border-hairline bg-white text-forest px-4 py-3 rounded-full hover:bg-cream transition-colors duration-500"
            >
              <GoogleGlyph />
              <span className="overline text-xs">Continue with Google</span>
            </button>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-hairline" />
              <div className="overline text-forest/40 text-[10px]">or with email</div>
              <div className="h-px flex-1 bg-hairline" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && (
                <Field icon={UserIcon} placeholder="Full name" value={name} onChange={setName} testid="auth-name" autoComplete="name" required />
              )}
              <Field icon={Mail} placeholder="Email" type="email" value={email} onChange={setEmail} testid="auth-email" autoComplete="email" required />
              <Field icon={Lock} placeholder="Password (min 8)" type="password" value={password} onChange={setPassword} testid="auth-password" autoComplete={mode === "signup" ? "new-password" : "current-password"} required minLength={8} />

              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit"
                className="mt-2 w-full inline-flex items-center justify-center gap-3 bg-forest text-champagne px-4 py-3 rounded-full hover:bg-[hsl(150_60%_16%)] transition-colors duration-500 disabled:opacity-50"
              >
                {mode === "signup" ? <UserPlus size={14} strokeWidth={1.6} /> : <LogIn size={14} strokeWidth={1.6} />}
                <span className="overline text-xs">
                  {busy ? "One moment…" : mode === "signup" ? "Create account" : "Sign in"}
                </span>
              </button>
            </form>

            <div className="mt-6 text-forest/60 text-sm">
              {mode === "signup" ? (
                <>Already have an account?{" "}
                  <button onClick={() => setMode("login")} data-testid="auth-switch-login" className="edit-link">Sign in →</button>
                </>
              ) : (
                <>New to Anabiya?{" "}
                  <button onClick={() => setMode("signup")} data-testid="auth-switch-signup" className="edit-link">Create an account →</button>
                </>
              )}
            </div>

            <div className="mt-3 text-forest/40 text-xs">
              <Link to="/checkout" className="edit-link">Continue as guest →</Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

const Field = ({ icon: Icon, testid, onChange, ...rest }) => (
  <label className="relative block">
    <Icon size={14} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/40" />
    <input
      {...rest}
      data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-11 pr-4 py-3 bg-white border border-hairline rounded-full text-forest text-sm focus:outline-none focus:border-forest"
    />
  </label>
);

const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);
